import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardActionArea, CardContent, CircularProgress, Stack, Typography, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, Chip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { fetchAllPresidents, deletePresident, createPresident, reorderPresidents, updatePresident, fetchPresidentById, uploadPresidentPhoto } from '../../controllers/presidentController';
import { resolvePresidentAssetUrl } from '../../api/httpClientPresidents';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const PresidentsList = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ fullName: '', termStartYear: '', termEndYear: '', biography: '', photo: null });
    const [editOpen, setEditOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editForm, setEditForm] = useState({ fullName: '', termStartYear: '', termEndYear: '', biography: '', photo: null, photoPreview: null });
    const [updating, setUpdating] = useState(false);

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await fetchAllPresidents();
            setItems(data || []);
        } catch (e) {
            setError(e?.message || 'Başkanlar alınamadı');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEditOpen = async (president) => {
        setEditOpen(true);
        setEditLoading(true);
        try {
            const data = await fetchPresidentById(president.id);
            setEditData(data);
            setEditForm({
                fullName: data.fullName || '',
                termStartYear: data.termStartYear || '',
                termEndYear: data.termEndYear || '',
                biography: data.biography || '',
                photo: null,
                photoPreview: data.photoUrl ? resolvePresidentAssetUrl(data.photoUrl) : null,
            });
        } catch (e) {
            console.error('Başkan alınamadı:', e);
        } finally {
            setEditLoading(false);
        }
    };

    const handleEditClose = () => {
        setEditOpen(false);
        setEditData(null);
        setEditForm({ fullName: '', termStartYear: '', termEndYear: '', biography: '', photo: null, photoPreview: null });
    };

    const ids = useMemo(() => (items || []).map((p) => p.id), [items]);

    const SortableRow = ({ id, children }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : 1,
        };
        return children({ attributes, listeners, setNodeRef, style });
    };

    if (loading) {
        return (
            <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Skeleton width={150} height={28} />
                    <Skeleton width={120} height={36} />
                </Stack>
                <TableContainer
                    component={Paper}
                    sx={{
                        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #f5f5f5',
                        py: 1,
                        px: 2.5
                    }}
                >
                    <Table size="small">
                        <TableHead sx={{ bgcolor: 'transparent' }}>
                            <TableRow>
                                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Sıralama</TableCell>
                                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Görsel</TableCell>
                                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Ad Soyad</TableCell>
                                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Dönem</TableCell>
                                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Durum</TableCell>
                                <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <TableRow
                                    key={`skeleton-${index}`}
                                    sx={{
                                        '& .MuiTableCell-root': {
                                            borderBottom: index === 4 ? 'none' : '1px dashed #e8e8e8',
                                            px: 1.5,
                                            py: 1.5,
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Skeleton width={24} height={24} borderRadius={4} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton width={50} height={50} borderRadius={4} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton width={200} height={20} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton width={120} height={20} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton width={60} height={24} borderRadius={16} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Skeleton width={32} height={32} borderRadius={6} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    }

    if (error) return <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>;

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Başkanlarımız</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ width: 16 }} />}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#E7EDF4',
                        boxShadow: 'none',
                        fontSize: 13,
                        borderRadius: '5px',
                        color: '#0E4D94',
                        '&:hover': {
                            backgroundColor: '#CFDBEA',
                            boxShadow: 'none',
                        },
                    }}
                    onClick={() => setCreateOpen(true)}
                >
                    Yeni Başkan
                </Button>
            </Stack>

            {error ? (
                <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
            ) : (
                <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => {
                        const { active, over } = event;
                        if (!over || active.id === over.id) return;
                        const oldIndex = ids.indexOf(active.id);
                        const newIndex = ids.indexOf(over.id);
                        setItems((prev) => {
                            const newArr = arrayMove(prev, oldIndex, newIndex);
                            // optimistic order update
                            const updated = newArr.map((p, idx) => ({ ...p, order: idx }));
                            // fire reorder API in background
                            (async () => {
                                try {
                                    await reorderPresidents({ pastPresidentIdsInOrder: updated.map((p) => p.id) });
                                } catch (e) {
                                    // if fails, reload server order
                                    try { await load(); } catch (_) { }
                                }
                            })();
                            return updated;
                        });
                    }}
                >
                    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                        <TableContainer
                            component={Paper}
                            sx={{
                                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                                border: '1px solid #f5f5f5',
                                py: 1,
                                px: 2.5
                            }}
                        >
                            <Table size="small">
                                <TableHead sx={{ bgcolor: 'transparent' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3', width: 80 }}>Sıralama</TableCell>
                                        <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3', width: 100 }}>Görsel</TableCell>
                                        <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Ad Soyad</TableCell>
                                        <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Dönem</TableCell>
                                        <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Durum</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>İşlemler</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                <Typography sx={{ color: '#a3a3a3' }}>Henüz başkan eklenmemiş</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        items.map((p, index) => (
                                            <SortableRow key={p.id} id={p.id}>
                                                {({ attributes, listeners, setNodeRef, style }) => (
                                                    <TableRow
                                                        ref={setNodeRef}
                                                        style={style}
                                                        {...attributes}
                                                        hover
                                                        sx={{
                                                            cursor: 'move',
                                                            '& .MuiTableCell-root': {
                                                                borderBottom: index === items.length - 1 ? 'none' : '1px dashed #e8e8e8',
                                                                px: 1.5,
                                                                py: 1.5,
                                                                color: '#444444',
                                                            },
                                                        }}
                                                    >
                                                        <TableCell
                                                            {...listeners}
                                                            sx={{ cursor: 'grab', userSelect: 'none' }}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                        >
                                                            <DragIndicatorIcon sx={{ color: '#a3a3a3', fontSize: 20 }} />
                                                        </TableCell>
                                                        <TableCell
                                                            {...listeners}
                                                            sx={{ cursor: 'grab', userSelect: 'none' }}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                        >
                                                            {p.photoUrl ? (
                                                                <Box
                                                                    component="img"
                                                                    src={resolvePresidentAssetUrl(p.photoUrl)}
                                                                    alt={p.fullName}
                                                                    sx={{
                                                                        width: 50,
                                                                        height: 50,
                                                                        objectFit: 'cover',
                                                                        borderRadius: '4px',
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Box
                                                                    sx={{
                                                                        width: 50,
                                                                        height: 50,
                                                                        borderRadius: '4px',
                                                                        bgcolor: '#f2f2f2',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                    }}
                                                                >
                                                                    <ImageIcon sx={{ fontSize: 24, color: '#a3a3a3' }} />
                                                                </Box>
                                                            )}
                                                        </TableCell>
                                                        <TableCell
                                                            {...listeners}
                                                            sx={{ fontWeight: 500, cursor: 'grab', userSelect: 'none' }}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                        >
                                                            {p.fullName}
                                                        </TableCell>
                                                        <TableCell
                                                            {...listeners}
                                                            sx={{ cursor: 'grab', userSelect: 'none' }}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                        >
                                                            {p.termStartYear}{p.termEndYear ? ` - ${p.termEndYear}` : ''}
                                                        </TableCell>
                                                        <TableCell
                                                            {...listeners}
                                                            sx={{ cursor: 'grab', userSelect: 'none' }}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                        >
                                                            {p.isActive ? (
                                                                <Chip
                                                                    label="Aktif"
                                                                    size="small"
                                                                    sx={{
                                                                        height: 24,
                                                                        fontSize: '11px',
                                                                        fontWeight: 600,
                                                                        backgroundColor: '#DFFFEA',
                                                                        color: '#17C653',
                                                                        borderRadius: '12px',
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Chip
                                                                    label="Pasif"
                                                                    size="small"
                                                                    sx={{
                                                                        height: 24,
                                                                        fontSize: '11px',
                                                                        fontWeight: 600,
                                                                        backgroundColor: '#FFF4E6',
                                                                        color: '#FF9800',
                                                                        borderRadius: '12px',
                                                                    }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell
                                                            align="right"
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                                <Tooltip title="Düzenle">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleEditOpen(p);
                                                                        }}
                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                        sx={{
                                                                            backgroundColor: '#E7EDF4',
                                                                            width: 32,
                                                                            height: 32,
                                                                            borderRadius: '6px',
                                                                            '&:hover': {
                                                                                backgroundColor: '#CFDBEA',
                                                                            },
                                                                            '& svg': {
                                                                                fontSize: '16px',
                                                                                color: '#0E4D94',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <EditIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Sil">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelected(p);
                                                                            setConfirmOpen(true);
                                                                        }}
                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                        sx={{
                                                                            backgroundColor: '#FFEEF3',
                                                                            width: 32,
                                                                            height: 32,
                                                                            borderRadius: '6px',
                                                                            '&:hover': {
                                                                                backgroundColor: '#FFD6E0',
                                                                            },
                                                                            '& svg': {
                                                                                fontSize: '16px',
                                                                                color: '#F8285A',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <DeleteOutlineIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </SortableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </SortableContext>
                </DndContext>
            )}

            {/* Delete confirm */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        fontSize: '18px',
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pr: 3,
                    }}
                >
                    Başkanı Sil
                    <IconButton
                        onClick={() => setConfirmOpen(false)}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 2, px: 3 }}>
                    <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
                        {selected ? `"${selected.fullName}" kaydını silmek istediğinize emin misiniz?` : 'Seçili kaydı silmek istediğinize emin misiniz?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            color: '#666666',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            if (!selected) return;
                            try {
                                await deletePresident(selected.id);
                                setConfirmOpen(false);
                                setSelected(null);
                                await load();
                            } catch (e) { }
                        }}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            backgroundColor: '#F8285A',
                            borderRadius: '5px',
                            px: 2,
                            '&:hover': {
                                backgroundColor: '#E01E4A',
                            },
                        }}
                    >
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle
                    sx={{
                        fontSize: '18px',
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pr: 3,
                    }}
                >
                    Yeni Başkan
                    <IconButton
                        onClick={() => setCreateOpen(false)}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 2, px: 3 }}>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Ad Soyad
                            </Typography>
                            <TextField
                                fullWidth
                                value={createForm.fullName}
                                onChange={(e) => setCreateForm({ ...createForm, fullName: e.target.value })}
                                placeholder=""
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.2,
                                        px: 1.5,
                                        fontSize: '14px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
                            />
                        </Box>
                        <Stack direction="row" spacing={2}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                    Başlangıç Yılı
                                </Typography>
                                <TextField
                                    fullWidth
                                    type="number"
                                    value={createForm.termStartYear}
                                    onChange={(e) => setCreateForm({ ...createForm, termStartYear: e.target.value })}
                                    placeholder=""
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '5px',
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#0E4D94',
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            py: 1.2,
                                            px: 1.5,
                                            fontSize: '14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#e6e6e6',
                                        },
                                    }}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                    Bitiş Yılı
                                </Typography>
                                <TextField
                                    fullWidth
                                    type="number"
                                    value={createForm.termEndYear}
                                    onChange={(e) => setCreateForm({ ...createForm, termEndYear: e.target.value })}
                                    placeholder=""
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '5px',
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#0E4D94',
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            py: 1.2,
                                            px: 1.5,
                                            fontSize: '14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#e6e6e6',
                                        },
                                    }}
                                />
                            </Box>
                        </Stack>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Biyografi
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                value={createForm.biography}
                                onChange={(e) => setCreateForm({ ...createForm, biography: e.target.value })}
                                placeholder=""
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: '14px',
                                        px: 0,
                                        py: 0,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Fotoğraf
                            </Typography>
                            {createForm.photo ? (
                                <Box sx={{ mb: 1 }}>
                                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                        <Box
                                            component="img"
                                            src={URL.createObjectURL(createForm.photo)}
                                            alt="Seçilen fotoğraf"
                                            sx={{
                                                width: 100,
                                                height: 100,
                                                objectFit: 'cover',
                                                borderRadius: '4px',
                                                border: '1px solid #e6e6e6',
                                            }}
                                        />
                                        <Tooltip title="Kaldır">
                                            <IconButton
                                                size="small"
                                                onClick={() => setCreateForm({ ...createForm, photo: null })}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    right: 4,
                                                    backgroundColor: '#FFEEF3',
                                                    width: 28,
                                                    height: 28,
                                                    '&:hover': {
                                                        backgroundColor: '#FFD6E0',
                                                    },
                                                    '& svg': {
                                                        fontSize: '14px',
                                                        color: '#F8285A',
                                                    },
                                                }}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                                        <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 500, color: '#444444' }}>
                                            {createForm.photo.name}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '12px', color: '#a3a3a3' }}>
                                            {createForm.photo.size > 1024 * 1024
                                                ? `${(createForm.photo.size / 1024 / 1024).toFixed(2)} MB`
                                                : `${(createForm.photo.size / 1024).toFixed(2)} KB`}
                                        </Typography>
                                    </Stack>
                                </Box>
                            ) : (
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<ImageIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '13px',
                                        borderRadius: '5px',
                                        borderColor: '#e6e6e6',
                                        color: '#666666',
                                        py: 1,
                                        px: 2,
                                        '&:hover': {
                                            borderColor: '#0E4D94',
                                            backgroundColor: 'rgba(14, 77, 148, 0.04)',
                                        },
                                    }}
                                >
                                    Fotoğraf Seç
                                    <input
                                        hidden
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                setCreateForm({ ...createForm, photo: file });
                                            }
                                        }}
                                    />
                                </Button>
                            )}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button
                        onClick={() => {
                            setCreateOpen(false);
                            setCreateForm({ fullName: '', termStartYear: '', termEndYear: '', biography: '', photo: null });
                        }}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            color: '#666666',
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            try {
                                await createPresident({
                                    fullName: createForm.fullName,
                                    termStartYear: createForm.termStartYear ? parseInt(createForm.termStartYear, 10) : undefined,
                                    termEndYear: createForm.termEndYear ? parseInt(createForm.termEndYear, 10) : null,
                                    biography: createForm.biography,
                                    isActive: true,
                                    photo: createForm.photo,
                                });
                                setCreateOpen(false);
                                setCreateForm({ fullName: '', termStartYear: '', termEndYear: '', biography: '', photo: null });
                                await load();
                            } catch (e) { }
                        }}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            backgroundColor: '#0E4D94',
                            borderRadius: '5px',
                            px: 2,
                            py: 0.7,
                            '&:hover': {
                                backgroundColor: '#0a3d7a',
                            },
                        }}
                    >
                        Oluştur
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit dialog */}
            <Dialog open={editOpen} onClose={handleEditClose} fullWidth maxWidth="sm">
                <DialogTitle
                    sx={{
                        fontSize: '18px',
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pr: 3,
                    }}
                >
                    Başkanı Düzenle
                    <IconButton
                        onClick={handleEditClose}
                        disabled={updating}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 2, px: 3 }}>
                    {editLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : editData ? (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                    Ad Soyad
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={editForm.fullName}
                                    onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                                    disabled={updating}
                                    placeholder=""
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '5px',
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#0E4D94',
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            py: 1.2,
                                            px: 1.5,
                                            fontSize: '14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#e6e6e6',
                                        },
                                    }}
                                />
                            </Box>
                            <Stack direction="row" spacing={2}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                        Başlangıç Yılı
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        value={editForm.termStartYear}
                                        onChange={(e) => setEditForm({ ...editForm, termStartYear: e.target.value })}
                                        disabled={updating}
                                        placeholder=""
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '5px',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0E4D94',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                py: 1.2,
                                                px: 1.5,
                                                fontSize: '14px',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#e6e6e6',
                                            },
                                        }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                        Bitiş Yılı
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        value={editForm.termEndYear}
                                        onChange={(e) => setEditForm({ ...editForm, termEndYear: e.target.value })}
                                        disabled={updating}
                                        placeholder=""
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '5px',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0E4D94',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                py: 1.2,
                                                px: 1.5,
                                                fontSize: '14px',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#e6e6e6',
                                            },
                                        }}
                                    />
                                </Box>
                            </Stack>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                    Biyografi
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    value={editForm.biography}
                                    onChange={(e) => setEditForm({ ...editForm, biography: e.target.value })}
                                    disabled={updating}
                                    placeholder=""
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '5px',
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#0E4D94',
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '14px',
                                            px: 0,
                                            py: 0,
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#e6e6e6',
                                        },
                                    }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                    Fotoğraf
                                </Typography>
                                {(editForm.photoPreview || editForm.photo) ? (
                                    <Box sx={{ mb: 1 }}>
                                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                            <Box
                                                component="img"
                                                src={editForm.photo ? URL.createObjectURL(editForm.photo) : editForm.photoPreview}
                                                alt={editForm.photo ? 'Yeni fotoğraf' : 'Mevcut fotoğraf'}
                                                sx={{
                                                    width: 100,
                                                    height: 100,
                                                    objectFit: 'cover',
                                                    borderRadius: '4px',
                                                    border: '1px solid #e6e6e6',
                                                }}
                                            />
                                            <Tooltip title="Kaldır">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        if (editForm.photo) {
                                                            // Yeni seçilen fotoğrafı kaldır
                                                            setEditForm({
                                                                ...editForm,
                                                                photo: null,
                                                                photoPreview: editData?.photoUrl ? resolvePresidentAssetUrl(editData.photoUrl) : null,
                                                            });
                                                        } else {
                                                            // Mevcut fotoğrafı kaldır
                                                            setEditForm({
                                                                ...editForm,
                                                                photoPreview: null,
                                                            });
                                                        }
                                                    }}
                                                    disabled={updating}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                        backgroundColor: '#FFEEF3',
                                                        width: 28,
                                                        height: 28,
                                                        '&:hover': {
                                                            backgroundColor: '#FFD6E0',
                                                        },
                                                        '& svg': {
                                                            fontSize: '14px',
                                                            color: '#F8285A',
                                                        },
                                                    }}
                                                >
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        {editForm.photo && (
                                            <Stack spacing={0.5} sx={{ mt: 1 }}>
                                                <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 500, color: '#444444' }}>
                                                    {editForm.photo.name}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '12px', color: '#a3a3a3' }}>
                                                    {editForm.photo.size > 1024 * 1024
                                                        ? `${(editForm.photo.size / 1024 / 1024).toFixed(2)} MB`
                                                        : `${(editForm.photo.size / 1024).toFixed(2)} KB`}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>
                                ) : (
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        startIcon={<ImageIcon />}
                                        disabled={updating}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '13px',
                                            borderRadius: '5px',
                                            borderColor: '#e6e6e6',
                                            color: '#666666',
                                            py: 1,
                                            px: 2,
                                            '&:hover': {
                                                borderColor: '#0E4D94',
                                                backgroundColor: 'rgba(14, 77, 148, 0.04)',
                                            },
                                        }}
                                    >
                                        Fotoğraf Seç
                                        <input
                                            hidden
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    setEditForm({
                                                        ...editForm,
                                                        photo: file,
                                                        photoPreview: URL.createObjectURL(file),
                                                    });
                                                }
                                            }}
                                        />
                                    </Button>
                                )}
                            </Box>
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button
                        onClick={handleEditClose}
                        disabled={updating}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            color: '#666666',
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            if (!editData) return;
                            setUpdating(true);
                            try {
                                await updatePresident({
                                    id: editData.id,
                                    fullName: editForm.fullName,
                                    termStartYear: editForm.termStartYear ? parseInt(editForm.termStartYear, 10) : undefined,
                                    termEndYear: editForm.termEndYear ? parseInt(editForm.termEndYear, 10) : null,
                                    biography: editForm.biography,
                                    isActive: editData.isActive,
                                });
                                if (editForm.photo instanceof File) {
                                    try {
                                        await uploadPresidentPhoto({ pastPresidentId: editData.id, file: editForm.photo });
                                    } catch (e) {
                                        console.error('Fotoğraf yüklenemedi:', e);
                                    }
                                }
                                handleEditClose();
                                await load();
                            } catch (e) {
                                console.error('Başkan güncellenemedi:', e);
                            } finally {
                                setUpdating(false);
                            }
                        }}
                        disabled={updating}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            backgroundColor: '#0E4D94',
                            borderRadius: '5px',
                            px: 2,
                            py: 0.7,
                            '&:hover': {
                                backgroundColor: '#0a3d7a',
                            },
                        }}
                    >
                        {updating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Kaydet'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PresidentsList;









